import { useRef, useState, useLayoutEffect, useCallback } from 'react'
import { genUID } from 'settings/app/helpers'

export function useCollapse(defaultOpen = false, animationDuration = 300) {
  const idRef = useRef(`collapse-${ genUID() }`)
  const containerRef = useRef()

  const [show, setShow] = useState(defaultOpen)

  const open = useCallback(() => {
    const { current: container } = containerRef
    if (!container) return
    container.style.height = `${ container.scrollHeight }px`
    container.style.overflow = 'hidden'
    setTimeout(() => {
      if (!container) return
      container.style.overflow = 'visible'
      container.style.height = '100%'
    }, animationDuration)
  }, [animationDuration])

  const hide = () => {
    const { current: container } = containerRef
    // set height for animation
    container.style.height = `${ container.scrollHeight }px`
    setTimeout(() => {
      container.style.height = `0px`
      container.style.overflow = 'hidden'
    })
  }

  useLayoutEffect(() => {
    if (defaultOpen) open()
  }, [defaultOpen, open])

  const toggle = (value = !show) => {
    // avoid unnecessary re-render
    if (value === show) return
    setShow(value)
    value ? open() : hide()
  }

  return {
    toggle,
    triggerProps: {
      'aria-controls': idRef.current,
      'aria-expanded': `${ show }`,
      tabIndex: 0,
      onClick: () => toggle(),
      onKeyDown: ({ keyCode }) => {
        // enter pressed
        if (keyCode === 13) toggle()
      },
    },
    containerProps: {
      id: idRef.current,
      'aria-hidden': `${ !show }`,
      ref: containerRef,
      style: {
        transition: `height ${ animationDuration }ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
      },
    },
  }
}
